import React from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import withStyles from '@mui/styles/withStyles';

import DescriptionIcon from '@mui/icons-material/Description';
import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import DemandesEnlevementIcon from '@mui/icons-material/Reply';

import ActionsBar from './ActionsBar';
import DemandesEnlevement from './DemandesEnlevement';
import DemandesEnlevementDrawerListItem from './DemandesEnlevementDrawerListItem';
import Existantes from './Existantes';
import Historique from './Historique';
import HistoriqueDrawerListItem from './HistoriqueDrawerListItem';
import InfosGenerales from './InfosGenerales';
import InfosLogistique from './InfosLogistique';
import Qualification from './Qualification';
import ReclamationDrawerListItem from './ReclamationDrawerListItem';
import ReclamationCheckFichierFMCP from './ReclamationCheckFichierFMCP';
import ReclamationPageHeader from './ReclamationPageHeader';

import withLoader from 'components/Shared/withLoader';

import localStorageHelper from 'utils/localStorageHelper';

const ReclamationPageHeaderWithLoader = withLoader(ReclamationPageHeader);


const drawerWidth = 200;
const styles = theme => ({
    root: {
        padding: 0
    },

    content: {
        padding: theme.spacing(1, 3, 3, 3),
        marginLeft: drawerWidth,
        backgroundColor: theme.palette.background.primary,
        height: `calc(100vh - 72px)`,

        display: 'flex',
        flexDirection: 'column'
    },

    card: {
        maxHeight: '100%',
        overflowY: 'visible',
        backgroundColor: theme.palette.background.default,
        padding: 2
    },

    drawerPaper: {
        position: 'fixed',
        width: drawerWidth,
        top: 72,
        paddingTop: theme.spacing(1)
    }
});

const anchors = {
    qualification: 'qualification',
    infosGenerales: 'infosGenerales',
    infosLogistique: 'infosLogistique',
    historique: 'historique',
    existantes: 'existantes',
    demandesEnlevement: 'demandesEnlevement',
};

const children = {
    qualification: Qualification,
    infosGenerales: InfosGenerales,
    infosLogistique: InfosLogistique,
    historique: Historique,
    existantes: Existantes,
    demandesEnlevement: DemandesEnlevement,
};

class ReclamationPage extends React.Component {

    getAnchor() {
        return (document.URL.split('#').length > 1) ? document.URL.split('#')[1] : null;
    }

    getScrollAnchor() {
        let scrollAnchor = (document.URL.split('#').length > 2) ? document.URL.split('#')[2] : null;
        return scrollAnchor;
    }


    render() {

        let { classes, reclamation, isLoading } = this.props;

        let Content = children[this.getAnchor()];
        if (!Content) Content = children.qualification;


        let url = `/reclamations/${reclamation.id}`;

        let autresReclamationsCount = reclamation.piece && reclamation.piece.reclamationsIds ? reclamation.piece.reclamationsIds.length : 0;
        let afficheAutresReclamations = autresReclamationsCount > 0;

        return (
            <div className={classes.root}>
                <Helmet>
                    <title>{`Dossier Après-Vente ${reclamation.id}`}</title>
                </Helmet>

                <Drawer
                    variant="permanent"
                    classes={{
                        paper: classes.drawerPaper
                    }}
                >
                    <List component="nav">
                        <ReclamationDrawerListItem
                            title="Qualification"
                            href={`${url}#${anchors.qualification}`}
                            icon={<DescriptionIcon />}
                            isSelected={this.getAnchor() === anchors.qualification || this.getAnchor() === null}
                        />

                        <ReclamationDrawerListItem
                            title="Infos générales"
                            href={`${url}#${anchors.infosGenerales}`}
                            icon={<InfoIcon />}
                            isSelected={this.getAnchor() === anchors.infosGenerales}
                        />

                        <ReclamationDrawerListItem
                            title="Infos logistiques"
                            href={`${url}#${anchors.infosLogistique}`}
                            icon={(
                                <SvgIcon className={classes.listItemIcon} style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24">
                                    <path d="M18,18.5A1.5,1.5 0 0,1 16.5,17A1.5,1.5 0 0,1 18,15.5A1.5,1.5 0 0,1 19.5,17A1.5,1.5 0 0,1 18,18.5M19.5,9.5L21.46,12H17V9.5M6,18.5A1.5,1.5 0 0,1 4.5,17A1.5,1.5 0 0,1 6,15.5A1.5,1.5 0 0,1 7.5,17A1.5,1.5 0 0,1 6,18.5M20,8H17V4H3C1.89,4 1,4.89 1,6V17H3A3,3 0 0,0 6,20A3,3 0 0,0 9,17H15A3,3 0 0,0 18,20A3,3 0 0,0 21,17H23V12L20,8Z" />
                                </SvgIcon>
                            )}
                            isSelected={this.getAnchor() === anchors.infosLogistique}
                            disable={reclamation.piece === null}
                        />

                        {
                            reclamation && reclamation.id !== 0 &&
                            <HistoriqueDrawerListItem
                                reclamationId={reclamation.id}
                                href={`${url}#${anchors.historique}`}
                                icon={<HistoryIcon />}
                                isSelected={this.getAnchor() === anchors.historique}
                            />
                        }

                        {
                            afficheAutresReclamations &&

                            <ReclamationDrawerListItem
                                title={`Autres dossiers après-vente (${autresReclamationsCount})`}
                                href={`${url}#${anchors.existantes}`}
                                icon={(
                                    <SvgIcon className={classes.listItemIcon} style={{ width: '24px', height: '24px' }} viewBox="0 0 24 24">
                                        <path d="m 16.505994,18.006206 v -0.184825 c 0,0 0.307941,-2.401961 -3.387401,-5.173479 0.923824,-0.739068 3.448993,-3.1410288 3.510563,-5.9741389 0.246372,-0.1848253 0.431127,-0.5542896 0.431127,-0.9238238 0,-0.6159059 -0.492697,-1.1701955 -1.170195,-1.1701955 -0.615883,0 -1.170172,0.49272 -1.170172,1.1701955 0,0.3079413 0.123139,0.5542896 0.307941,0.7390684 0,0 0,0 0,0 0.123139,0.4926967 0.307941,2.278798 -3.325808,5.1734773 -0.49272,-0.307941 -1.04701,-0.615905 -1.7245084,-0.98544 C 12.317932,10.061163 13.980847,7.9055515 13.980847,5.3804051 14.103986,2.4857244 11.640456,0.0221476 8.6225894,0.0221476 c -3.0178665,0 -5.4198278,2.4635768 -5.4198278,5.48142 0,2.2172052 1.2933587,4.1264682 3.2026226,4.9887224 0,0 0,0 0,0 0,0 0,0 0.061686,0 0,0 0,0 0.061686,0 0.061686,0 0.061686,0 0.1231393,0.06169 0,0 0.061686,0 0.061686,0.06169 1.4781368,0.677475 8.3761245,3.94169 8.3761245,7.329091 0,0.184825 0,0.307941 0,0.431127 0,0 0,0 0,0 -0.184826,0.123139 -0.307965,0.369534 -0.307965,0.615883 0,0.615882 0.49272,1.170195 1.170196,1.170195 0.615906,0 1.170195,-0.49272 1.170195,-1.170195 0,-0.431127 -0.246348,-0.800662 -0.615882,-0.985417 z M 6.7749184,8.7678523 5.1735958,9.6300823 5.6047229,7.2897155 C 5.0504333,6.6738097 4.9272475,6.4274613 4.9272475,5.5036142 5.1120727,3.4095948 6.6517326,1.8698651 8.6841823,1.8698651 c 2.0324267,0 3.6337497,1.6629156 3.6337497,3.6337491 0,2.0324497 -1.662893,3.6337723 -3.6337497,3.6337723 -0.7390684,0 -1.3549509,-0.061686 -1.9092639,-0.3695342 z" />
                                        <path d="m 14.473544,20.96248 c 0,0.615882 -0.492697,1.170171 -1.170196,1.170171 -0.615882,0 -1.170172,-0.492696 -1.170172,-1.170171 0,-0.369534 0.184825,-0.677499 0.431104,-0.862254 0,-0.369535 0.184825,-1.662916 -0.431104,-2.771518 -0.49272,-0.431104 -0.862254,-0.739069 -1.231788,-1.04701 v 5.48142 c 0.307941,0.184825 0.554289,0.554312 0.554289,0.985439 0,0.615883 -0.492696,1.170196 -1.170172,1.170196 -0.6159057,0 -1.1701953,-0.49272 -1.1701953,-1.170196 0,-0.246371 0.1231392,-0.49272 0.2463483,-0.739068 V 15.234688 C 9.1768327,15.111549 8.9305307,14.98834 8.6841823,14.865154 8.3146481,15.049979 8.0067068,15.173095 7.6987656,15.357874 v 6.466836 c 0.2463483,0.184825 0.4311038,0.554313 0.4311038,0.923847 0,0.615883 -0.4926968,1.170196 -1.1701722,1.170196 -0.6159059,0 -1.1701955,-0.49272 -1.1701955,-1.170196 0,-0.307941 0.1231392,-0.615906 0.3079413,-0.800661 v -5.604605 c -1.847671,1.601322 -1.4165439,3.141052 -1.354951,3.695342 0.2463484,0.184825 0.4311038,0.49272 0.4311038,0.862254 0,0.123139 0,0.184825 -0.061686,0.307941 0,0 0,0 0,0 -0.1231393,0.49272 -0.5543129,0.862254 -1.1086026,0.862254 -0.6159065,0 -1.1701962,-0.49272 -1.1701962,-1.170195 0,-0.06169 0,-0.123139 0,-0.123139 v 0 c 0,-0.307942 0.1231393,-0.615883 0.4311271,-0.800662 -0.1848253,-3.325807 1.2933588,-4.496003 3.2026227,-5.666175 0.2463483,-0.123139 0.4311271,-0.246372 0.5542896,-0.307965 -0.1848253,-0.06169 -0.3695342,-0.184825 -0.5542896,-0.246348 0,0 -0.4311271,-0.184825 -1.0470097,-0.49272 -3.5105873,2.771518 -3.2642156,5.050316 -3.2642156,5.050316 0.2463484,0.184825 0.3079413,0.431127 0.3079413,0.739068 0,0.615883 -0.4927201,1.170196 -1.1701955,1.108603 -0.61588254,0 -1.10860256,-0.49272 -1.10860256,-1.170195 0,-0.369535 0.18482529,-0.677476 0.49272002,-0.923848 C 0.73918478,15.419513 2.9562736,13.202308 4.0033072,12.278461 2.4635768,11.169859 0.80066123,9.3221885 0.67749876,6.7354491 0.24637165,6.6123099 0,6.2427291 0,5.7500324 0,5.1341265 0.49272002,4.5798369 1.1701954,4.5798369 c 0.6159059,0 1.1701956,0.49272 1.1701956,1.1701955 0,0.1848253 -0.061686,0.4311271 -0.1848253,0.6158825 0.061453,1.0470097 0.6774755,3.3258069 4.0648769,5.6046051 1.2317651,0.800661 2.8330879,1.231788 4.8039444,2.463553 1.909241,1.108603 3.202622,2.278798 2.956274,5.604606 0.246348,0.184826 0.492697,0.554313 0.492697,0.923847 z" />
                                    </SvgIcon>
                                )}
                                isSelected={this.getAnchor() === anchors.existantes}
                            />
                        }

                        {
                            reclamation && reclamation.id !== 0 &&
                            <DemandesEnlevementDrawerListItem
                                reclamationId={reclamation.id}
                                href={`${url}#${anchors.demandesEnlevement}`}
                                icon={<DemandesEnlevementIcon />}
                                isSelected={this.getAnchor() === anchors.demandesEnlevement}
                            />
                        }
                    </List>
                </Drawer>

                <div className={classes.content}>
                    <div>
                        <ActionsBar reclamation={reclamation} />
                        <Divider />

                        <Stack direction="row" spacing={1}>
                            <Box sx={{ flexGrow: 1 }}>
                                <ReclamationPageHeaderWithLoader isLoading={isLoading} reclamation={reclamation} afficheVerification templateReclamationPage />
                            </Box>
                            {localStorageHelper.isAdmin() && (
                                <Box sx={{ flexGrow: 0 }}>
                                    <ReclamationCheckFichierFMCP reclamationId={reclamation.id} />
                                </Box>
                            )}
                        </Stack>

                    </div>

                    <Card className={classes.card} elevation={0}>
                        {reclamation && reclamation.id !== 0 && <Content reclamationId={reclamation.id} reclamation={reclamation} scrollTo={this.getScrollAnchor()} />}
                    </Card>
                </div>
            </div>
        );
    }
}

ReclamationPage.propTypes = {
    classes: PropTypes.object.isRequired,

    reclamation: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired
};

export default withStyles(styles)(ReclamationPage);